/**
 * @file Navbar.tsx
 * @module _admin/layout/components/header/Navbar
 * @description Renders the Navbar layout component that builds the shared UTrade Admin header and navigation.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import clsx from 'clsx';
import { Moon, Sun } from 'lucide-react';
import { HeaderNotificationsMenu, HeaderUserMenu, useThemeMode } from '../../../partials';
import { useAuth } from '../../../../app/modules/auth';
import { useWarehouse } from '../../../../app/context/WarehouseContext';
import NotificationSVG from '../../../assets/media/dashboard/notifications.svg';
import { useEffect, useRef, useState } from 'react';
import BellSVG from '../../../../_admin/assets/media/svg_uTrade/bell-icon.svg';
import APICallService from '../../../../api/apiCallService';
import { assistAdmin, AUTH } from '../../../../api/apiEndPoints';
import { KTSVG } from '../../../helpers';
import { FormLabel } from 'react-bootstrap';
import { CustomSelect } from '../../../../app/custom/Select/CustomSelect';
import { Admin, AllCustomers, Manager, ServiceMember, WarehouseModule } from '../../../../utils/constants';
import { useLocation } from 'react-router-dom';
import Method from '../../../../utils/methods';
import WarehouseChangeConfirmation from '../../../../app/modals/warehouse-change-confirmation';
import { useNavigationHistory } from '../../../../utils/useNavigationHistory';
import ServiceRequestLauncher from '../../../../app/modules/service-request/components/service-request-launcher';

const itemClass = 'ms-1 ms-lg-3';
const btnClass = 'btn btn-icon btn-custom btn-icon-muted';
const userAvatarClass =
  'symbol-40px symbol-md-45px symbol-lg-50px symbol-circle';
const btnIconClass = 'svg-icon-1';
const Navbar = () => {
  // const { config } = useLayout()
  const { currentUser, saveCurrentUser } = useAuth();
  const { mode, updateMode } = useThemeMode();
  const { selectedWarehouse, setSelectedWarehouse, warehouseList, fetchWarehouseList } = useWarehouse();
  const [show, setShow] = useState(false);
  const [count, setCount] = useState(false);
  const [showWarehouseConfirm, setShowWarehouseConfirm] = useState(false);
  const [pendingWarehouse, setPendingWarehouse] = useState<any | null>(null);
  const dropdownRef: any = useRef(null);
  const location = useLocation();
  const { sourcePagePath, goBackToSource } = useNavigationHistory();

  useEffect(() => {
    const channel = new BroadcastChannel('warehouse_channel');
    channel.onmessage = (event) => {
      if (event.data === 'warehouse_changed') {
        window.location.reload();
      }
    };
    return () => {
      channel.close();
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (currentUser?.role.some((obj: any) => obj.type === Manager) || currentUser?.role.some((obj: any) => obj.type === ServiceMember)) {
        let params: any = {
          "role[0][type]": currentUser?.role.find((obj: any) => obj.type === Manager) ? Manager : ServiceMember
        }
        let apiService = new APICallService(
          assistAdmin.info,
          params,
          { id: currentUser?._id },
          "",
          "",
          "",
          AllCustomers,
          ""
        );
        let response = await apiService.callAPI();
        if (response) {
          saveCurrentUser(response);
        }
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      await fetchNotificationCount();
      // if (currentUser?.userType === Admin) {
      await fetchWarehouseList();
      // }
    })();
  }, []);
  const fetchNotificationCount = async () => {
    let apiService = new APICallService(AUTH.notificationCount);
    let response = await apiService.callAPI();
    if (response) {
      setCount(response?.unreadItems?.length ? true : false);
    }
  };
  const MarkAsRead = async () => {
    if (show === true) {
      let apiService = new APICallService(AUTH.readNotifications);
      let response = await apiService.callAPI();
      if (response) {
        await fetchNotificationCount();
      }
    }
  };
  useEffect(() => {
    (async () => {
      await MarkAsRead();
    })();
  }, [show]);
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShow(false);
    }
  };
  useEffect(() => {
    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  const handleWarehouseChange = (event: any) => {
    if (!event) return;

    if (selectedWarehouse && event.value === selectedWarehouse.value) {
      return;
    }

    setPendingWarehouse(event);
    setShowWarehouseConfirm(true);
  };

  const handleWarehouseChangeConfirm = () => {
    if (pendingWarehouse) {
      setSelectedWarehouse(pendingWarehouse);
      const channel = new BroadcastChannel('warehouse_channel');
      channel.postMessage('warehouse_changed');
    }
    setShowWarehouseConfirm(false);
    setPendingWarehouse(null);
    if (sourcePagePath) {
      goBackToSource();
    } else {
      window.location.reload();
    }
  };

  const hideWarehouseFilter = location.pathname?.includes('/sales-management');
  return (
    <div className="app-navbar flex-shrink-0">
      <div
        className={clsx('app-navbar-item', itemClass)}
        ref={dropdownRef}
      >
        {(currentUser?.userType === Admin || (currentUser?.warehouse?.defaultWarehouse && Method.hasModulePermission(WarehouseModule, currentUser))) && !hideWarehouseFilter ? (
          <div className="px-4">
            <CustomSelect
              backgroundColor="white"
              value={selectedWarehouse}
              onChange={(event: any) => {
                handleWarehouseChange(event);
              }}
              hideSelectedOptions={true}
              options={warehouseList}
            />
            <WarehouseChangeConfirmation
              show={showWarehouseConfirm}
              onHide={() => {
                setShowWarehouseConfirm(false);
                setPendingWarehouse(null);
              }}
              fromWarehouse={selectedWarehouse?.label}
              toWarehouse={pendingWarehouse?.label}
              onConfirm={handleWarehouseChangeConfirm}
            />
          </div>
        ) : (
          <></>
        )}
        <ServiceRequestLauncher
          className={`${btnClass} me-1 release-highlighter--navbar-self-service`}
        />
        <div
          className={`${btnClass} release-highlighter--theme-toggle-v2`}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={() => {
            updateMode(mode === 'dark' ? 'light' : 'dark');
          }}
        >
          {mode === 'dark' ? (
            <Moon size={18} />
          ) : (
            <Sun size={18} />
          )}
        </div>
      </div>
      <div className={clsx('app-navbar-item', itemClass)}>
        <div
          className={clsx('cursor-pointer symbol', userAvatarClass, 'release-highlighter--profile')}
          data-kt-menu-trigger="{default: 'click'}"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
        >
          <div className="symbol  symbol-50px symbol-circle ">
            <div className="symbol-label fs-1 fw-bold bg-light-primary text-primary fw-700">
              {currentUser?.name.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
        <HeaderUserMenu />
      </div>
    </div>
  );
};
export { Navbar };
